import type { AppConfig } from "./config";
import {
  type Account,
  type AuthenticatedSession,
  parseAccount,
  parseSession,
  serializeSession,
} from "./domain";
import { AuthenticationError, AuthenticationRequiredError } from "./errors";
import { submitAccountEmailRequest } from "./email";
import { type MfaChallenge, parseMfaChallenge, parseMfaSession } from "./mfa";
import {
  type ApiRequestInit,
  fetchResponse,
  parseJsonResponse,
  requestJson,
  requireStatus,
  responseError,
} from "./http";

const REFRESH_WINDOW_MS = 60_000;

export class BrowserAuthManager {
  private session: AuthenticatedSession | null = null;
  private scope = 0;
  private selectingOrganization = false;
  private accountValue: Account | null = null;
  private refreshInFlight: Promise<AuthenticatedSession> | null = null;
  private challenge: MfaChallenge | null = null;
  private pendingSession: AuthenticatedSession | null = null;
  private backupCodes: readonly string[] = [];

  public get mfaChallenge(): MfaChallenge | null {
    return this.challenge;
  }
  public get recoveryCodes(): readonly string[] {
    return this.backupCodes;
  }

  public constructor(
    private readonly config: AppConfig,
    private readonly sessionStorageKey: string,
  ) {
    if (sessionStorageKey.length === 0) throw new TypeError("Session storage key is required");
  }

  public get account(): Account | null {
    return this.accountValue;
  }

  public async initialize(): Promise<boolean> {
    if (this.config.authenticationMode === "disabled") {
      this.accountValue = await this.loadAccount();
      return true;
    }

    const saved = this.readStoredSession();
    if (saved === null) return false;
    this.session = saved;
    if (this.shouldRefresh(saved)) await this.refreshSession();
    this.accountValue = await this.refreshAccount();
    return true;
  }

  public async signIn(email: string, password: string): Promise<void> {
    this.clearSession();
    await this.continueSignIn("/v1/auth/login", { email, password });
  }

  public async verifyMfa(code: string): Promise<void> {
    if (this.challenge === null) throw new AuthenticationError("Sign in again.");
    await this.continueSignIn("/v1/auth/mfa", {
      challenge_token: this.challenge.challengeToken,
      code,
    });
  }

  public acknowledgeRecoveryCodes(): void {
    if (this.pendingSession === null) throw new AuthenticationError("Sign in again.");
    this.commitSession(this.pendingSession);
    this.accountValue = this.pendingSession;
    this.pendingSession = null;
    this.backupCodes = [];
  }

  public cancelSignIn(): void {
    this.clearSession();
  }

  public async requestPasswordReset(email: string): Promise<void> {
    await submitAccountEmailRequest(
      this.config.apiOrigin,
      "/v1/auth/password-reset",
      { email },
      202,
    );
  }

  private async continueSignIn(
    path: string,
    body: Readonly<Record<string, string>>,
  ): Promise<void> {
    if (this.config.authenticationMode === "disabled")
      throw new AuthenticationError("Sign-in is disabled in development mode.");
    const scope = ++this.scope;
    const response = await fetchResponse(`${this.config.apiOrigin}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (response.status === 202) {
      const challenge = await parseJsonResponse(response, parseMfaChallenge, "MFA challenge");
      this.requireScope(scope);
      this.challenge = challenge;
      return;
    }
    const { session, recoveryCodes } = await parseJsonResponse(
      response,
      parseMfaSession,
      "authentication",
    );
    this.requireScope(scope);
    this.challenge = null;
    if (recoveryCodes.length > 0) {
      this.pendingSession = session;
      this.backupCodes = recoveryCodes;
      return;
    }
    this.commitSession(session);
    this.accountValue = session;
  }

  public async organizations(): Promise<readonly { id: string; name: string }[]> {
    return requestJson(
      this,
      "/v1/account/organizations",
      (value) => {
        if (!Array.isArray(value)) throw new TypeError("Invalid organisations");
        return value.map((item: unknown) => {
          if (
            typeof item !== "object" ||
            item === null ||
            !("id" in item) ||
            !("name" in item) ||
            typeof item.id !== "string" ||
            typeof item.name !== "string"
          )
            throw new TypeError("Invalid organisation");
          return { id: item.id, name: item.name };
        });
      },
      "organisations",
    );
  }

  public async selectOrganization(organizationId: string): Promise<void> {
    if (this.refreshInFlight !== null) await this.refreshInFlight;
    const token = await this.accessToken();
    const scope = ++this.scope;
    this.selectingOrganization = true;
    try {
      const response = await this.sendAuthenticated(
        "/v1/auth/organization",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ organization_id: organizationId }),
        },
        token,
      );
      const session = await parseJsonResponse(response, parseSession, "organisation selection");
      this.requireScope(scope);
      this.commitSession(session);
      this.accountValue = session;
    } catch (error) {
      if (this.scope === scope) this.clearSession();
      throw error;
    } finally {
      if (this.scope === scope) this.selectingOrganization = false;
    }
  }

  public async signOut(): Promise<void> {
    try {
      const token = await this.accessToken();
      if (this.config.authenticationMode === "required") {
        const response = await fetchResponse(`${this.config.apiOrigin}/v1/auth/logout`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          cache: "no-store",
        });
        await requireStatus(response, 204);
      }
    } finally {
      this.clearSession();
    }
  }

  public async refreshAccount(): Promise<Account> {
    const scope = this.scope;
    const account = await this.loadAccount();
    this.requireScope(scope);
    const session = this.session;
    if (session !== null) {
      if (
        account.user.id !== session.user.id ||
        account.organization.id !== session.organization.id
      ) {
        this.clearSession();
        throw new AuthenticationRequiredError(
          "Your account or organization changed. Sign in again.",
        );
      }
      this.commitSession({ ...session, ...account });
    }
    this.accountValue = account;
    return account;
  }

  public async authenticatedFetch(path: string, init: ApiRequestInit = {}): Promise<Response> {
    const scope = this.scope;
    init.signal?.throwIfAborted();
    const token = await this.accessToken();
    this.requireScope(scope);
    let response = await this.sendAuthenticated(path, init, token);
    this.requireScope(scope);
    if (response.status !== 401 || this.config.authenticationMode === "disabled")
      return response;
    init.signal?.throwIfAborted();
    // A concurrent request may already have rotated the credentials.
    const refreshed = await this.accessToken(token === this.session?.accessToken);
    this.requireScope(scope);
    response = await this.sendAuthenticated(path, init, refreshed);
    this.requireScope(scope);
    if (response.status === 401) {
      this.clearSession();
      throw new AuthenticationRequiredError(await responseError(response), response.status);
    }
    return response;
  }

  private requireScope(scope: number): void {
    if (scope !== this.scope)
      throw new AuthenticationRequiredError("The active account or organisation changed.");
  }

  private loadAccount(): Promise<Account> {
    return requestJson(this, "/v1/me", parseAccount, "account");
  }

  private async accessToken(forceRefresh = false): Promise<string> {
    if (this.selectingOrganization)
      throw new AuthenticationRequiredError("Organisation selection is in progress.");
    if (this.config.authenticationMode === "disabled") return "local-development";
    const session = this.session;
    if (session === null) throw new AuthenticationRequiredError();
    if (forceRefresh || this.shouldRefresh(session)) {
      return (await this.refreshSession()).accessToken;
    }
    return session.accessToken;
  }

  private shouldRefresh(session: AuthenticatedSession): boolean {
    return session.expiresAt.getTime() <= Date.now() + REFRESH_WINDOW_MS;
  }

  private refreshSession(): Promise<AuthenticatedSession> {
    if (this.refreshInFlight !== null) return this.refreshInFlight;
    const operation = this.performRefresh();
    this.refreshInFlight = operation;
    void operation.then(
      () => this.clearRefreshOperation(operation),
      () => this.clearRefreshOperation(operation),
    );
    return operation;
  }

  private clearRefreshOperation(operation: Promise<AuthenticatedSession>): void {
    if (this.refreshInFlight === operation) this.refreshInFlight = null;
  }

  private async performRefresh(): Promise<AuthenticatedSession> {
    const scope = this.scope;
    const current = this.session;
    if (current === null) throw new AuthenticationRequiredError();
    let refreshed: AuthenticatedSession;
    try {
      refreshed = await this.requestSession("/v1/auth/refresh", {
        refresh_token: current.refreshToken,
      });
    } catch (error) {
      if (
        error instanceof AuthenticationRequiredError &&
        this.scope === scope &&
        this.session?.refreshToken === current.refreshToken
      ) {
        this.clearSession();
      }
      throw error;
    }
    this.requireScope(scope);
    if (this.session?.refreshToken !== current.refreshToken)
      throw new AuthenticationRequiredError();
    if (
      refreshed.user.id !== current.user.id ||
      refreshed.organization.id !== current.organization.id
    ) {
      this.clearSession();
      throw new AuthenticationRequiredError(
        "Your account or organization changed. Sign in again.",
      );
    }
    this.commitSession(refreshed);
    return refreshed;
  }

  private async requestSession(
    path: string,
    body: Readonly<Record<string, string>>,
  ): Promise<AuthenticatedSession> {
    const response = await fetchResponse(`${this.config.apiOrigin}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (response.status === 401 || response.status === 403) {
      throw new AuthenticationRequiredError(await responseError(response), response.status);
    }
    return parseJsonResponse(response, parseSession, "authentication");
  }

  private sendAuthenticated(
    path: string,
    init: ApiRequestInit,
    accessToken: string,
  ): Promise<Response> {
    const headers = new Headers(init.headers);
    headers.set("Authorization", `Bearer ${accessToken}`);
    return fetchResponse(`${this.config.apiOrigin}${path}`, {
      ...init,
      headers,
      cache: "no-store",
    });
  }

  private readStoredSession(): AuthenticatedSession | null {
    try {
      const encoded = sessionStorage.getItem(this.sessionStorageKey);
      if (encoded === null) return null;
      return parseSession(JSON.parse(encoded));
    } catch {
      this.clearSession();
      throw new AuthenticationError("The saved sign-in was invalid. Please sign in again.");
    }
  }

  private commitSession(session: AuthenticatedSession): void {
    try {
      sessionStorage.setItem(this.sessionStorageKey, serializeSession(session));
    } catch {
      this.clearSession();
      throw new AuthenticationError(
        "Browser session storage is unavailable. Allow site storage and try again.",
      );
    }
    this.session = session;
  }

  private clearSession(): void {
    this.scope += 1;
    this.selectingOrganization = false;
    this.session = null;
    this.accountValue = null;
    this.challenge = null;
    this.pendingSession = null;
    this.backupCodes = [];
    try {
      sessionStorage.removeItem(this.sessionStorageKey);
    } catch {
      // In-memory credentials are still discarded.
    }
  }
}
